import { FC, useMemo, useState } from "react";
import Page from "src/layouts/Page";
import usePermissions from "src/hooks/usePermissions/usePermissions";
import { Permissions } from "src/constants/permissions";
import PeriodPicker from "../../components/PeriodPicker/PeriodPicker";
import EmptyState from "../../components/EmptyState/EmptyState";
import OverviewTab from "../../tabs/OverviewTab";
import AdoptionTab from "../../tabs/AdoptionTab";
import ContentTab from "../../tabs/ContentTab";
import SocialTab from "../../tabs/SocialTab";
import OperationsTab from "../../tabs/OperationsTab";
import UsersTab from "../../tabs/UsersTab";
import EventInspectorTab from "../../tabs/EventInspectorTab";
import LongTermTrendsTab from "../../tabs/LongTermTrendsTab";
import useTrackingEvents from "../../hooks/useTrackingEvents";
import { makeTablistKeyHandler } from "../../utils/tablistKeyboard";
import { periodFromQuickRange, type Period } from "../../utils/period";

type TabKey =
	| "overview"
	| "long_term"
	| "adoption"
	| "content"
	| "social"
	| "operations"
	| "users"
	| "inspector";

interface TabDef {
	key: TabKey;
	label: string;
}

const TABS: TabDef[] = [
	{ key: "overview", label: "Overview" },
	{ key: "adoption", label: "Adoption" },
	{ key: "social", label: "Social & Comms" },
	{ key: "content", label: "Content" },
	{ key: "operations", label: "Operations" },
	{ key: "users", label: "Users" },
	{ key: "long_term", label: "Long-term trends" },
	{ key: "inspector", label: "Event inspector" }
];

const Analytics: FC = () => {
	const { hasPermission } = usePermissions();
	const canViewAnalytics = hasPermission(Permissions.CanViewAnalytics);
	const canViewUserAnalytics = hasPermission(Permissions.CanViewUserAnalytics);

	const [period, setPeriod] = useState<Period>(() =>
		periodFromQuickRange("30d")
	);
	const [activeTab, setActiveTab] = useState<TabKey>("overview");

	const eventsQuery = useTrackingEvents(true, {
		enabled: canViewAnalytics
	});

	const availableEvents = useMemo(() => {
		const set = new Set<string>();
		(eventsQuery.data ?? []).forEach((e) => {
			set.add(e.name);
			(e.previous_names ?? []).forEach((p) => set.add(p));
		});
		return set;
	}, [eventsQuery.data]);

	if (!canViewAnalytics) {
		return (
			<Page>
				<EmptyState
					title="Permission required"
					description="Requires the CanViewAnalytics permission."
					tone="warn"
				/>
			</Page>
		);
	}

	return (
		<Page>
			<h1 className="sr-only">Analytics</h1>
			<div className="mb-6">
				<PeriodPicker value={period} onChange={setPeriod} />
			</div>

			<div
				className="w-full flex flex-wrap items-center gap-1 bg-white border border-border p-2 rounded-2xl shadow-xs mb-6"
				role="tablist"
				aria-label="Analytics sections"
				onKeyDown={makeTablistKeyHandler(
					TABS.map((t) => t.key),
					activeTab,
					setActiveTab
				)}
			>
				{TABS.map((t) => (
					<button
						key={t.key}
						type="button"
						role="tab"
						aria-selected={activeTab === t.key}
						aria-controls={`analytics-tabpanel-${t.key}`}
						id={`analytics-tab-${t.key}`}
						tabIndex={activeTab === t.key ? 0 : -1}
						onClick={() => setActiveTab(t.key)}
						className={[
							"px-3 py-1.5 text-sm font-medium rounded-lg transition-all",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
							activeTab === t.key
								? "bg-primary text-white shadow-md shadow-primary/40"
								: "text-zinc-600 hover:text-foreground hover:bg-subtle"
						].join(" ")}
					>
						{t.label}
					</button>
				))}
			</div>

			<div
				id={`analytics-tabpanel-${activeTab}`}
				role="tabpanel"
				aria-labelledby={`analytics-tab-${activeTab}`}
			>
				{/* Visually hidden h2 anchored to the active tab. Without
				    a section-level heading, the page's heading outline
				    skipped from the page-level h1 straight to the
				    sub-card h3s. Screen-reader heading navigation now
				    finds: h1 Analytics → h2 (active tab name) → h3
				    cards. Sighted users get the same signpost from the
				    visible tab strip above. */}
				<h2 className="sr-only">
					{TABS.find((t) => t.key === activeTab)?.label ?? activeTab}
				</h2>
				{eventsQuery.isLoading ? (
					<div className="h-32 animate-pulse bg-zinc-100 rounded-xl" />
				) : eventsQuery.isError ? (
					<EmptyState
						title="Failed to load event vocabulary"
						description="Could not load /tracking/events/. Try refreshing the page."
						tone="warn"
					/>
				) : (
					<>
						{activeTab === "overview" && (
							<OverviewTab period={period} availableEvents={availableEvents} />
						)}
						{activeTab === "long_term" && (
							<LongTermTrendsTab
								period={period}
								availableEvents={availableEvents}
								allEvents={eventsQuery.data ?? []}
							/>
						)}
						{activeTab === "adoption" && (
							<AdoptionTab period={period} availableEvents={availableEvents} />
						)}
						{activeTab === "content" && (
							<ContentTab period={period} availableEvents={availableEvents} />
						)}
						{activeTab === "social" && (
							<SocialTab period={period} availableEvents={availableEvents} />
						)}
						{activeTab === "operations" && (
							<OperationsTab
								period={period}
								availableEvents={availableEvents}
							/>
						)}
						{activeTab === "users" && (
							<UsersTab
								period={period}
								canViewUserAnalytics={canViewUserAnalytics}
							/>
						)}
						{activeTab === "inspector" && (
							<EventInspectorTab events={eventsQuery.data ?? []} />
						)}
					</>
				)}
			</div>
		</Page>
	);
};

export default Analytics;
